import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";

//CSS
import "./navBar.css";

import SvgUStorageIcon from "../../SvgIcons/UStorageIcon";

//Functions and hooks
import useNarrowMode from "../../hooks/useNarrowModeChecker";
import { GetUnreadMessageCount } from "../../BackendAccess/MessageHooks";
import { GetAccessiblePages } from "../../BackendAccess/UserHooks";
import { useTranslation } from "react-i18next";

function NavBar({ navType }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isNarrow = useNarrowMode();
  const [navExpandClick, setNavExpandClick] = useState(false);
  const handleNavExpandClick = () => setNavExpandClick(!navExpandClick);
  const closeExpandedNav = () => setNavExpandClick(false);
  const [items, setItems] = useState([]);

  const [unreadMessageCount, setUnreagMessageCount] = useState(0);

  const [showNav, setShowNav] = useState(true);

  const { t, i18n } = useTranslation("navbar");
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);

  const languages = [
    { code: "en", label: "EN", flag: "🇺🇸" },
    { code: "rs", label: "RS", flag: "🇷🇸" },
  ];

  useEffect(() => {
    fetchUnreadMessageCount();
    const interval = setInterval(() => {
      fetchUnreadMessageCount();
    }, 1000 * 60 * 5);

    return () => clearInterval(interval);
  }, []);

  const fetchUnreadMessageCount = async () => {
    try {
      const [data, error] = await GetUnreadMessageCount();
      if (error) setUnreagMessageCount(0);
      setUnreagMessageCount(data);
    } catch (err) {
      setUnreagMessageCount(0);
    }
  };
  const isAndroid = /Android/i.test(navigator.userAgent);

  const prevScrollY = useRef(0);

  const navStructure = useMemo(() => {
    return {
      home: [
        ...(isAndroid
          ? []
          : [{ name: t("About"), link: "/home/#AboutSection" }]),
        ...(isAndroid
          ? []
          : [{ name: t("Packages"), link: "/home/#PackageSection" }]),
        ...(isAndroid
          ? []
          : [{ name: t("Contact"), link: "/home/#ContactSection" }]),
        { name: t("Login"), link: "/Login" },
        { name: t("Register"), link: "/register" },
      ],
      workspace: [
        {
          name: t("Warehouses"),
          link: "/workspace/warehouses",
          permission: "WarehouseView",
        },
        { name: t("Items"), link: "/workspace/items", permission: "ItemView" },
        {
          name: t("Low Stock", { ns: "items" }),
          link: "/workspace/items/lowStock",
          permission: "ItemView",
        },
        {
          name: t("Transfer Stock", { ns: "items" }),
          link: "/workspace/items/transferStock",
          permission: "NoteCreate",
        },
        {
          name: t("Bulk Import Items", { ns: "items" }),
          link: "/workspace/items/importItems",
          permission: "ItemCreate",
        },
        {
          name: t("Stock Take", { ns: "items" }),
          link: "/workspace/items/stockTake",
          permission: "NoteCreate",
        },
        { name: t("Tags"), link: "/workspace/tags", permission: "TagView" },
        {
          name: t("Traffic"),
          link: "/workspace/traffic",
          permission: "NoteView",
        },
        {
          name: t("Clients"),
          link: "/workspace/clients",
          permission: "ClientView",
        },
        { name: t("Units"), link: "/workspace/units", permission: "UnitView" },
        {
          name: t("Employees"),
          link: "/workspace/employees",
          permission: "EmployeeView",
        },
        { name: t("Roles"), link: "/workspace/roles", permission: "RoleView" },
        { name: t("Inbox"), link: "/workspace/messages" },
      ],
      settings: [
        { name: t("GeneralSettings"), link: "/settings/general" },
        { name: t("UserSettings"), link: "/settings/user" },
        {
          name: t("CompanySettings"),
          link: "/settings/company",
          permission: "ViewCompanyInfo",
        },
        {
          name: t("Packages"),
          link: "/settings/packagePage",
        },
      ],
      dataView: [
        { name: t("Activity"), link: "/dataView/activity" },
        {
          name: t("Report", { ns: "items" }),
          link: "/dataView/report",
          permission: "ActivityView",
        },
      ],
    };
  }, [t, isAndroid]);

  useEffect(() => {
    if (navType === "home") {
      setItems(navStructure.home);
    } else if (navType === "workspace") {
      setWarehouseNavItems();
    } else if (navType === "settings") {
      setSettingsItems();
    } else if (navType === "dataView") {
      setDataItems();
    }
  }, [navType, navStructure]);

  const setWarehouseNavItems = async () => {
    let accessiblePages;
    const [data, error] = await GetAccessiblePages();
    if (!data || data === "") accessiblePages = [];
    else if (typeof data === "string") accessiblePages = data.split(",");
    else accessiblePages = data;

    const filteredWorkspaceItems = navStructure.workspace.filter(
      (item) => !item.permission || accessiblePages.includes(item.permission)
    );

    setItems(filteredWorkspaceItems);
  };
  const setSettingsItems = async () => {
    let accessiblePages;
    const [data, error] = await GetAccessiblePages();
    if (!data || data === "") accessiblePages = [];
    else if (typeof data === "string") accessiblePages = data.split(",");
    else accessiblePages = data;

    const filteredSettingsItems = navStructure.settings.filter(
      (item) => !item.permission || accessiblePages.includes(item.permission)
    );

    setItems(filteredSettingsItems);
  };
  const setDataItems = async () => {
    let accessiblePages;
    const [data, error] = await GetAccessiblePages();
    if (!data || data === "") accessiblePages = [];
    else if (typeof data === "string") accessiblePages = data.split(",");
    else accessiblePages = data;

    const filteredSettingsItems = navStructure.dataView.filter(
      (item) => !item.permission || accessiblePages.includes(item.permission)
    );

    setItems(filteredSettingsItems);
  };

  const handleNavigation = (path) => (e) => {
    console.log(t("About"));
    e.preventDefault();
    closeExpandedNav();

    if (navType === "home" && path.includes("#")) {
      const [basePath, hash] = path.split("#");
      navigate(basePath);

      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } else {
      navigate(path, { replace: false });
    }
  };

  const isPathMatch = (link) =>
    location.pathname === link ||
    ((link.includes("/workspace") || link.includes("/settings")) &&
      location.pathname.startsWith(link + "/"));

  const createItems = (item) => {
    // A prefix match (e.g. "/workspace/items") would also match sub-paths
    // that have their own, more specific nav entry (e.g.
    // "/workspace/items/lowStock"), lighting up both tabs at once. Only
    // treat this item as active if no other item in the same nav list is a
    // more specific match for the current path.
    const isActive =
      isPathMatch(item.link) &&
      !items.some(
        (other) =>
          other.link !== item.link &&
          other.link.length > item.link.length &&
          other.link.startsWith(item.link) &&
          isPathMatch(other.link)
      );

    return (
      <li key={item.name} className={`item nav ${isActive ? "active" : ""}`}>
        <a
          href="#"
          className={`link nav ${isActive ? "active" : ""}`}
          onClick={handleNavigation(item.link)}
        >
          {item.name}
          {item.name === "Inbox" ? (
            <div
              className={
                "nav-item-dot " + (unreadMessageCount > 0 ? "shown" : "")
              }
            ></div>
          ) : null}
        </a>
      </li>
    );
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY === 0) {
        setShowNav(true);
        return;
      }

      if (currentScrollY > prevScrollY.current) {
        setShowNav(false);
      } else {
        setShowNav(true);
      }

      prevScrollY.current = currentScrollY;
    };

    if (isNarrow) window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isNarrow]);

  const onLogoClick = () => {
    closeExpandedNav();
    navigate("/");
  };

  const toggleLanguageDropdown = () => {
    setIsLanguageDropdownOpen(!isLanguageDropdownOpen);
  };

  const changeLanguage = (languageCode) => {
    console.log("change lang ", languageCode, languageCode === "rs");
    i18n.changeLanguage(languageCode);
    setIsLanguageDropdownOpen(false);
    setIsMenuOpen(false);
  };

  const getCurrentLanguage = () => {
    return (
      languages.find((lang) => lang.code === i18n.language) || languages[0]
    );
  };

  return (
    <nav className={`nav-header ${!showNav ? "nav-hidden" : ""}`}>
      <div className="nav-container">
        <div className="logo" onClick={onLogoClick}>
          <SvgUStorageIcon
            color1="var(--TEXTCOLOR)"
            color2="var(--DETAILCOLOR2)"
            style={{ PointerEvent: "none" }}
          />
        </div>
        <div className="navigation">
          {navType === "home" && (
            <div className="language-switcher">
              <button
                className="language-toggle"
                onClick={toggleLanguageDropdown}
                aria-label="Change language"
              >
                <span className="language-flag">
                  {getCurrentLanguage().flag}
                </span>
                <span className="language-code">
                  {getCurrentLanguage().label}
                </span>
                <span
                  className={`language-arrow ${
                    isLanguageDropdownOpen ? "active" : ""
                  }`}
                >
                  ▼
                </span>
              </button>

              {isLanguageDropdownOpen && (
                <div className="language-dropdown">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      className={`language-option ${
                        i18n.language === lang.code ? "active" : ""
                      }`}
                      onClick={() => changeLanguage(lang.code)}
                    >
                      <span className="language-flag">{lang.flag}</span>
                      <span className="language-code">{lang.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <ul className={navExpandClick ? "list nav active" : "list nav"}>
            {items.map(createItems)}
          </ul>

          <div
            className={navExpandClick ? "expandBtn active" : "expandBtn"}
            onClick={handleNavExpandClick}
          >
            <div
              className={navExpandClick ? "expandIcon active" : "expandIcon"}
            ></div>
          </div>
        </div>
      </div>
    </nav>
  );
}

export default NavBar;
